import { IEcgData } from '@se/ecg.viewer.html5';
import { EcgChartState } from '@se/models';
import { Moment } from 'moment/moment';
import { ICommandVisitExamination } from './visit';
import { IdDescriptionItem } from './vnext.models';

export interface IdDescriptionItemEcg extends IdDescriptionItem {
    id: any;
}

export interface IEcgBusinessUnit extends IdDescriptionItemEcg {
    httpPacs: string | null;
    ecgHeader: string | null;
    ecgVersioningText: string | null;
    ecgSignatureText: string | null;
    ecgLogoImgName: string | null;
    code: number;
}

export interface EcgProvenance extends IdDescriptionItemEcg {
    groupProvenance: IdDescriptionItemEcg;
}

export interface IPatientDetails {
    age: number;
    weight: number;
    height: number;
    bsa: number;
    bmi: number;
    fc: number;
    bps: number;
    bpd: number;
    rhythmId: any;
}

export interface IEcgExamDetails extends EcgBaseExam {
    lastDateFromReportedStatus: Date; // non ce l'ha
    patient: EcgPatientDetails; // diverso
}

export interface EcgPatientDetails {
    id: any;
    firstName: string | null;
    lastName: string | null;
    birthDate: Date;
    age: Age;
}

export interface Age {
    year: number;
    month: number;
    day: number;
}

export interface IReasonAndTherapy {
    reasonForStudy: string | null;
    therapy: string | null; // veniva usata anche come theraphy
}

export interface IConclusion {
    conclusion: string | null;
}

export interface EcgBaseExam {
    sopInstanceUID: string;
    examNumber: string | null;
    folderNumber: string | null;
    internalId: string;
    conclusion: string | null;
    severityId: any;
    provenanceId: any;
    technicianId: any;
    reporterId: any;
    postGraduates: string[];
    idDiagnosis: string;
    therapy: string | null;
    reasonForStudy: string | null;
    importDate: Date;
    patientDetails: IPatientDetails;
    lowFilter: number;
    highFilter: number;
    note: string | null;
}

export enum AnnotationType {
    UNKNOWN,
    UNFORMATTED_TEXT,
    VALUE,
    MARKER,
}

export interface IAnnotation extends IMeasure {
    mappedName: string | null;
    detail: string | null;
    type: AnnotationType;
    markers: Array<number>;
}

export interface IMeasure {
    name: string | null;
    value?: number;
    udM: string | null;
}

export interface IMeasureCategory {
    id: string;
    description: string | null;
    order: number;
}

export interface ICustomMeasure extends IMeasure {
    id: string;
    idEcgMeasure: any;
    internalUid: string;
    category: IMeasureCategory;
    isCalculated: boolean;
    isPrintable: boolean;
    isHighlighted: boolean;
    isManualModified: boolean;
    formulaDescription: string | null;
    formula: string | null;
    decimalDigit: number;
}

export interface IEcgReport extends EcgBaseExam {
    id: any;
    idExamination: any;
    status: string | null;
    executionDate: Date;
    reportDate: Date;
    examinationDate: Date;
    isReported: boolean;
    isApproved: boolean;
    isDigitallySigned: boolean;
    patient: EcgPatient;
    customMeasures: ICustomMeasure[];
    hardCodedData: any;
    reportingWithoutTrace?: boolean;
    repositoryNotRestricted?: boolean;
    repositoryVeryRestricted?: boolean;
    repositoryMediated?: boolean;
}

export interface Status extends IdDescriptionItemEcg {
    shortDescription: string | null;
}

export interface LightEcgExam {
    examinationId: any;
    reportId: string;
    patient: EcgPatient;
    examinationDate: Date;
    withEcg: boolean;
    isEcgReport?: boolean;
    isReported?: boolean;
    reportingWithoutTrace?: boolean;
}

export interface IEcgHistorySearchParam {
    idPatient: string;
    idExamination: string | null;
    thresholdDate: string | Moment;
    maxReturnedExams?: number;
}

//ecg worklist
export interface EcgPatient {
    // From BasePatient
    id: any;
    firstName: string | null;
    lastName: string | null;
    // From IBaseCommonPatient
    birthDate: any;
    sex: string | null;
    idLocalPatient: string | null;
    isLocalPatient: boolean;
    sexCode: string | null;
    taxCode: string | null;
    // ECG-specific properties
    isCertificating?: boolean | null;
    patientId: string;
    // Missing properties from ICommonWorklistPatientDTO
    race: string | null;
    otherPatientId: string;
    residenceCity: string | null;
    issuer: string | null;
}

export interface IEcgUserWorklist {
    id: any;
    fullName: string | null;
    employeeSerialNumber?: string | null;
    userName?: string | null;
}
export interface IEcgExaminationWorklistDTO {
    // From IBaseCommonExamination
    id: string;
    exam: IdDescriptionItem;
    isMerged: boolean;
    referringPhysician: IEcgUserWorklist | null;
    reportDate: string | null;
    severity: IdDescriptionItem | null;
    status: string | null;
    idReport: string | null;
    idLockedUser: string | null;
    lockedHost: string | null;
    lockedUserFullName: string | null;

    // From BaseExamination
    examinationDate: string | null;
    examinationsNumber?: number;
    sameStudyInstanceNumber?: number;
    isAdmitted: boolean;
    isApproved: boolean;
    isDigitallySigned: boolean;
    isEcgReport: boolean;
    isExecuted: boolean;
    isReported: boolean;
    imageAvailability: boolean;
    locked: boolean;
    examNumber: string | null;

    // EcgExamination-specific properties
    reportingWithoutTrace: boolean;
    patient: EcgPatient;
    businessUnit: IEcgBusinessUnit;
    provenance: EcgProvenance;
    command?: ICommandVisitExamination;

    // Additional properties to match requirements
    equipment?: IdDescriptionItem;
    diagnosis: IdDescriptionItem | null;
    admissionDate?: string | null;
    executionDate?: string | null;
    addendumNumber?: number;
    fromIntegration?: boolean;
    accessionNumber?: string | null;
    revaluedCDA?: boolean | null;
}

export interface EcgExamination extends IEcgExaminationWorklistDTO {
    isUndoSuccessfull?: boolean;
}
export interface IPreviousEcg {
    data: IEcgData;
    report: IEcgReport;
}
export interface IEcgApproveRequest {
    reportIds: string[];
    isDeferred: boolean;
    isFromWorklist?: boolean;
    displayedSettings?: EcgChartState;
    otherCredentials: string[];
    referringPhysicianId?: string;
}
